<script setup lang="ts">
  import { ref, onMounted } from 'vue'
  import { useMenuStore } from '@/stores/menu'
  import { useExportsStore } from '@/stores/exports'
  import { getDroppedImportUrl } from '@/helpers/url'
  import DesNavigationPanel from '@/components/navigation/DesNavigationPanel.vue'
  import DesNavigation from '@/components/navigation/DesNavigation.vue'
  import DesNavigationBottom from '@/components/navigation/DesNavigationBottom.vue'
  import DesTooltip from '@/components/tips/DesTooltip.vue'
  import DesPopup from '@/components/DesPopup.vue'
  import DesModal from '@/components/modal/DesModal.vue'
  import DesLoader from '@/components/DesLoader.vue'
  import DesSnackbar from '@/components/DesSnackbar.vue'
  import DesHomeMenu from '@/components/home/DesHomeMenu.vue'
  import DesTopBars from '@/components/bar/DesTopBars.vue'
  import DesTour from '@/components/tips/DesTour.vue'
  import DesAdvice from '@/components/tips/DesAdvice.vue'
  import DesButtonIconLite from '@/components/button/DesButtonIconLite.vue'
  import DesBarScreenCastomatic from '@/components/bar/DesBarScreenCastomatic.vue'

  defineProps<{ gradient?: boolean }>()

  const menu = useMenuStore()
  const exports = useExportsStore()
  const emit = defineEmits(['files-dropped', 'url-dropped'])

  const dropZoneRef = ref<HTMLDivElement | null>(null)

  onMounted(() => {
    exports.hydrate()

    if (dropZoneRef.value) {
      dropZoneRef.value.addEventListener('dragenter', (event) => {
        event.preventDefault()
      })

      dropZoneRef.value.addEventListener('dragover', (event) => {
        event.preventDefault()
      })

      dropZoneRef.value.addEventListener('drop', (event) => {
        event.preventDefault()
        const files = event.dataTransfer?.files
        if (files && files.length > 0) {
          emit('files-dropped', files)
        }
        const url = getDroppedImportUrl(event.dataTransfer)
        if (url) {
          emit('url-dropped', url)
        }
      })
    }
  })
</script>

<template>
  <div class="relative min-h-screen text-blue-10" ref="dropZoneRef">
    <DesTopBars />
    <header
      class="relative sticky ml-0 top-0 flex items-center justify-between h-16 pr-5 pl-6 border-b border-white-gray bg-white z-50 gap-6"
      :class="{ 'md:ml-navigation': menu.isOpen }"
    >
      <DesBarScreenCastomatic />
      <DesButtonIconLite
        @click="menu.isOpen = !menu.isOpen"
        label="Toggle navigation"
        icon="fa-light fa-sidebar"
        :class="{ 'text-blue-50': menu.isOpen }"
      />

      <slot name="search"></slot>
      <div></div>
    </header>

    <div
      v-if="gradient"
      class="fixed top-1/4 left-1/2 -translate-x-1/2 -z-10 rotate-45 h-0 rounded-full pointer-events-none"
      style="
        width: 72vw;
        padding-bottom: 36vw;
        background: linear-gradient(123deg, rgba(57, 169, 229, 0.14) 30.21%, rgba(131, 23, 255, 0.14) 88.11%);
        filter: blur(150px);
      "
    ></div>

    <DesNavigationPanel>
      <RouterLink to="/" class="shrink-0 h-16 hidden md:flex items-center" :class="{ 'max-w-navigation': menu.isOpen }">
        <img class="h-6 mx-5" :src="`/dashboard/logo/designrr.png`" alt="Designrr" />
      </RouterLink>

      <DesNavigation>
        <slot name="menu">
          <DesHomeMenu />
        </slot>
      </DesNavigation>
      <div class="grow"></div>
      <DesNavigationBottom />
    </DesNavigationPanel>

    <main class="px-6 py-9" :class="{ 'panel-margin': menu.isOpen, 'bg-white': !gradient }">
      <slot name="main"></slot>
    </main>

    <!--disable feedback now for DES-3780-->
    <!--<DesFeedback />-->
    <DesModal />
    <DesPopup />
    <DesTooltip />
    <DesLoader />
    <DesSnackbar />
    <DesAdvice />
    <DesTour />
  </div>
</template>
